/**
 * AgendaRail (P4.T12) — live agenda/side-effect projection for the
 * Simulate page's right rail.
 *
 * Pure props-in view over `SimulateAgendaState` (P4.T10 types, mirroring
 * `project_agenda_state()` in
 * backend/app/services/workflow_sandbox/agenda_state.py). Renders the
 * agenda EXACTLY as served: no client-side reordering beyond the given
 * array order and no semantics re-derivation — the backend already
 * computed priority/requiredness/status; this component only formats it.
 *
 * `slots[]` can include a synthetic `cart` row with `label: null` (see
 * the type's doc comment) — rendered as `label ?? key` like every other
 * row.
 */
import type { SandboxCapabilityCall, SimulateAgendaState } from '@/types/workflowSimulate'
import { SectionCard } from '../sections'
import { TokenChip } from '../TokenChip'
import { humanize } from '@/utils/friendlyLabels'
import { Badge, Button } from '@/components/ui'

type Slot = SimulateAgendaState['slots'][number]
type Need = SimulateAgendaState['needs'][number]
type Derived = SimulateAgendaState['derived'][number]
type SideEffect = SimulateAgendaState['side_effects'][number]
type Paused = NonNullable<SimulateAgendaState['paused']>

const CALL_KIND_BADGE =
  'inline-flex items-center rounded-sm bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase text-muted-foreground'

const SIDE_EFFECT_BADGE: Record<SideEffect['status'], string> = {
  fired:
    'inline-flex items-center rounded-sm bg-success/10 px-1.5 py-0.5 text-[10px] font-medium uppercase text-success',
  pending:
    'inline-flex items-center rounded-sm bg-warning/10 px-1.5 py-0.5 text-[10px] font-medium uppercase text-warning-strong',
}

function fullValueText(value: unknown): string {
  return typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value)
}

function previewValueText(value: unknown): string {
  const full = fullValueText(value)
  return typeof value === 'object' && value !== null ? full.slice(0, 40) : full
}

function SlotRow({ slot }: { slot: Slot }) {
  const symbol = slot.filled ? '✓' : slot.required_now ? '●' : '○'
  const symbolClass = slot.filled
    ? 'text-success'
    : slot.required_now
      ? 'text-warning'
      : 'text-muted-foreground/50'
  const muted = !slot.filled && !slot.required_now
  return (
    <li
      data-testid={`agenda-slot-${slot.key}`}
      className={muted ? 'flex items-baseline gap-2 text-sm opacity-50' : 'flex items-baseline gap-2 text-sm'}
    >
      <span className={symbolClass}>{symbol}</span>
      <TokenChip label={slot.label ?? humanize(slot.key)} title={slot.key} />
      {slot.filled && (
        <span
          className="truncate max-w-40 font-mono text-xs text-muted-foreground"
          title={fullValueText(slot.value)}
        >
          {previewValueText(slot.value)}
        </span>
      )}
    </li>
  )
}

function NeedRow({ need }: { need: Need }) {
  return (
    <li data-testid={`agenda-need-${need.key}`} className="flex items-baseline gap-2 text-sm">
      <span className={need.satisfied ? 'text-success' : 'text-muted-foreground/50'}>
        {need.satisfied ? '✓' : '○'}
      </span>
      <TokenChip label={humanize(need.key)} title={need.key} />
    </li>
  )
}

function DerivedRow({ derived }: { derived: Derived }) {
  return (
    <li data-testid={`agenda-derived-${derived.key}`} className="flex items-baseline gap-2 text-sm">
      <span className={derived.computed ? 'text-success' : 'text-muted-foreground/50'}>
        {derived.computed ? '✓' : '○'}
      </span>
      <TokenChip label={humanize(derived.key)} title={derived.key} />
    </li>
  )
}

function SideEffectRow({ effect }: { effect: SideEffect }) {
  return (
    <li data-testid={`agenda-side-effect-${effect.key}`} className="flex items-center gap-2 text-sm">
      <span className="text-foreground" title={effect.key}>{humanize(effect.key)}</span>
      <span className={SIDE_EFFECT_BADGE[effect.status]}>{effect.status}</span>
    </li>
  )
}

function CallDetails({ call }: { call: SandboxCapabilityCall }) {
  return (
    <details data-testid={`agenda-call-${call.seq}`} className="text-xs">
      <summary className="cursor-pointer text-muted-foreground font-medium">
        #{call.seq} {call.capability} <span className={CALL_KIND_BADGE}>{call.kind}</span>
      </summary>
      <pre className="mt-2 max-h-96 overflow-y-auto whitespace-pre-wrap wrap-break-word rounded-sm bg-muted p-2 text-[11px] text-foreground">
        {JSON.stringify({ args: call.args, result: call.result }, null, 2)}
      </pre>
    </details>
  )
}

function PausedBlock({
  paused,
  onFireEvent,
  onSimulateTimeout,
  firing,
}: {
  paused: Paused
  onFireEvent: (eventName: string) => void
  onSimulateTimeout: () => void
  firing: boolean
}) {
  return (
    <div
      data-testid="agenda-paused"
      className="rounded-sm border border-warning/30 bg-warning/10 p-3 space-y-2"
    >
      <p className="text-xs text-warning-strong">
        Waiting for: {paused.for_event ?? 'timer'}
      </p>
      <div className="flex flex-wrap gap-2">
        {paused.for_event && (
          <Button size="sm" disabled={firing} onClick={() => onFireEvent(paused.for_event!)}>
            Deliver: {paused.for_event}
          </Button>
        )}
        <Button size="sm" variant="secondary" disabled={firing} onClick={onSimulateTimeout}>
          Simulate timeout
        </Button>
      </div>
    </div>
  )
}

export function AgendaRail({
  agenda,
  onFireEvent,
  onSimulateTimeout,
  firing,
}: {
  agenda: SimulateAgendaState | null
  onFireEvent: (eventName: string) => void
  onSimulateTimeout: () => void
  firing: boolean
}) {
  if (!agenda) return null

  return (
    <div className="space-y-3">
      <Badge variant="secondary">Stage: {agenda.stage ?? '—'}</Badge>

      <SectionCard title="Collecting">
        <ol data-testid="agenda-slots" className="space-y-1.5">
          {agenda.slots.map((slot) => (
            <SlotRow key={slot.key} slot={slot} />
          ))}
        </ol>
      </SectionCard>

      <SectionCard title="Needs">
        <ul data-testid="agenda-needs" className="space-y-1.5">
          {agenda.needs.map((need) => (
            <NeedRow key={need.key} need={need} />
          ))}
        </ul>
      </SectionCard>

      <SectionCard title="Automations">
        <ul data-testid="agenda-automations" className="space-y-1.5">
          {agenda.derived.map((derived) => (
            <DerivedRow key={derived.key} derived={derived} />
          ))}
          {agenda.side_effects.map((effect) => (
            <SideEffectRow key={effect.key} effect={effect} />
          ))}
        </ul>
      </SectionCard>

      <SectionCard title="Would-be calls">
        <div data-testid="agenda-calls" className="space-y-1.5">
          {agenda.capability_calls.map((call) => (
            <CallDetails key={call.seq} call={call} />
          ))}
        </div>
      </SectionCard>

      {agenda.paused && (
        <PausedBlock
          paused={agenda.paused}
          onFireEvent={onFireEvent}
          onSimulateTimeout={onSimulateTimeout}
          firing={firing}
        />
      )}
    </div>
  )
}
